<template>
  <div class="reports-detail-page">
    <div class="detail-toolbar">
      <b-form-input
        v-model="search"
        :placeholder="searchPlaceholder"
        class="toolbar-input"
      />
      <b-form-select
        v-model="selectedFilter"
        :options="filterOptions"
        class="toolbar-select"
      />
      <b-form-select
        v-if="showCategoryFilters"
        v-model="categoryId"
        :options="categoryFilterOptions"
        class="toolbar-select"
        @change="onCategoryFilterChange"
      />
      <b-form-select
        v-if="showCategoryFilters"
        v-model="secondaryCategoryId"
        :options="secondaryCategoryFilterOptions"
        class="toolbar-select"
        :disabled="!categoryId"
        @change="onCategoryFilterChange"
      />
      <b-button
        variant="outline-secondary"
        class="toolbar-export"
        :disabled="!filteredRows.length"
        @click="exportMetricCsv"
      >
        Export CSV
      </b-button>
    </div>

    <b-alert
      v-if="errorMessage"
      show
      variant="danger"
      class="detail-alert"
    >
      <div>{{ errorMessage }}</div>
      <b-button
        size="sm"
        variant="outline-danger"
        @click="fetchMetricRows"
      >
        Retry
      </b-button>
    </b-alert>

    <b-table
      responsive
      class="detail-table mb-0"
      :fields="tableFields"
      :items="filteredRows"
      :busy="isLoading"
      show-empty
      :empty-text="emptyStateText"
    >
      <template #table-busy>
        <div class="text-center text-muted my-2">
          <b-spinner
            small
            class="mr-50"
          />
          <span>Loading report details...</span>
        </div>
      </template>

      <template #cell(ad)="{ item }">
        <div class="ad-cell">
          <b-avatar
            :src="item.thumbnail || null"
            :text="item.adInitials"
            size="28"
            square
            variant="light-secondary"
            class="ad-mini-logo"
          />
          <span>{{ item.name }}</span>
        </div>
      </template>

      <template #cell(name)="{ item }">
        <div class="ad-cell">
          <b-avatar
            :src="item.thumbnail || null"
            :text="item.adInitials"
            size="28"
            square
            variant="light-secondary"
            class="ad-mini-logo"
          />
          <span>{{ item.name }}</span>
        </div>
      </template>

      <template #cell(status)="{ item }">
        <b-badge :variant="statusVariant(item.rawStatus)">
          {{ item.status }}
        </b-badge>
      </template>

      <template #cell(metricUsers)="{ item }">
        <div
          v-if="item.metricUsers.length"
          class="avatar-stack"
        >
          <b-avatar
            v-for="user in item.metricUsers"
            :key="`${item.id}-${user.id || user.name}`"
            :src="user.avatar || null"
            :text="avatarText(user.name)"
            size="28"
            class="stack-avatar"
            variant="light-secondary"
          />
          <span
            v-if="item.extraUsersCount > 0"
            class="more-count"
          >
            +{{ item.extraUsersCount }}
          </span>
        </div>
        <span
          v-else
          class="more-count"
        >
          {{ item.metricCount }}
        </span>
      </template>

      <template #cell(reportsText)="{ item }">
        <span>{{ item.reportsText }}</span>
      </template>

      <template #cell(contact)="{ item }">
        <span>{{ item.contact || '-' }}</span>
      </template>

      <template #cell(actions)="{ item }">
        <div class="row-actions">
          <feather-icon
            icon="MailIcon"
            size="16"
            class="cursor-pointer"
            @click="onMailAction(item)"
          />
          <feather-icon
            icon="EditIcon"
            size="16"
            class="cursor-pointer"
            @click="onEditAction(item)"
          />
          <feather-icon
            icon="Trash2Icon"
            size="16"
            class="cursor-pointer"
            @click="onDeleteAction(item)"
          />
        </div>
      </template>

      <template #cell(lastActive)="{ item }">
        <span>{{ item.lastActive || '-' }}</span>
      </template>
    </b-table>

    <div class="detail-footer">
      <small class="detail-footer__label">{{ paginationLabel }}</small>
      <b-pagination
        v-model="currentPage"
        :total-rows="paginationTotalRows"
        :per-page="perPage"
        size="sm"
        first-number
        last-number
        :disabled="isLoading"
        class="mb-0 mypoda-pagination"
      >
        <template #prev-text>
          Previous
        </template>
        <template #next-text>
          Next
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BFormSelect,
  BTable,
  BPagination,
  BAvatar,
  BAlert,
  BButton,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import { metricPathMap, vendorPanelApi } from '@/services/vendorPanelApi'
import { fetchAdCategories, primaryCategoryOptions, secondaryCategoryOptions } from '@/utils/adCategories'
import { getInitials } from '@/utils/vendorNotifications'
import { notifyInfo } from '@/utils/uiActions'
import { downloadCsv } from '@/utils/exportCsv'
import { resolveMediaUrl } from '@/utils/mediaUrl'

const FILTER_OPTIONS = [
  { value: '', text: 'All statuses' },
  { value: 'active', text: 'Active' },
  { value: 'inactive', text: 'Inactive' },
  { value: 'draft', text: 'Draft' },
  { value: 'pending_approval', text: 'Pending approval' },
]

const formatDate = value => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatStatus = value => {
  const source = String(value || '').trim()
  if (!source) return 'Unknown'

  return source
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export default {
  components: {
    BFormInput,
    BFormSelect,
    BTable,
    BPagination,
    BAvatar,
    BAlert,
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      search: '',
      selectedFilter: '',
      categoryId: null,
      secondaryCategoryId: null,
      categoryOptions: [],
      currentPage: 1,
      perPage: 8,
      totalRows: 0,
      rows: [],
      isLoading: false,
      errorMessage: '',
      searchDebounceTimer: null,
    }
  },
  async mounted() {
    if (this.showCategoryFilters) {
      await this.loadCategoryOptions()
    }
    this.fetchMetricRows()
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer)
  },
  computed: {
    metricKey() {
      return this.$route.meta.metricKey || 'likes'
    },
    metricLabel() {
      return this.metricKey === 'reports' ? 'REPORTS' : this.metricKey.toUpperCase()
    },
    supportsServerSearch() {
      return this.metricKey === 'followers'
    },
    showCategoryFilters() {
      return this.metricKey !== 'followers'
    },
    categoryFilterOptions() {
      return [
        { value: null, text: 'All categories' },
        ...primaryCategoryOptions(this.categoryOptions),
      ]
    },
    secondaryCategoryFilterOptions() {
      if (!this.categoryId) {
        return [{ value: null, text: 'Select primary category first' }]
      }
      const children = secondaryCategoryOptions(this.categoryOptions, this.categoryId)
      if (!children.length) {
        return [{ value: null, text: 'No child categories for this primary' }]
      }
      return [
        { value: null, text: 'All secondary categories' },
        ...children,
      ]
    },
    searchPlaceholder() {
      return this.supportsServerSearch ? 'Search followers' : 'Search this page'
    },
    filterOptions() {
      return FILTER_OPTIONS
    },
    tableFields() {
      if (this.metricKey === 'followers') {
        return [
          { key: 'no', label: 'NO' },
          { key: 'name', label: 'NAME' },
          { key: 'status', label: 'STATUS' },
          { key: 'joinedOn', label: 'JOINED ON' },
          { key: 'lastActive', label: 'LAST ACTIVE' },
          { key: 'contact', label: 'CONTACT' },
          { key: 'actions', label: 'ACTIONS' },
        ]
      }

      if (this.metricKey === 'reports') {
        return [
          { key: 'no', label: 'NO' },
          { key: 'ad', label: 'AD' },
          { key: 'reportsText', label: 'REPORTS' },
          { key: 'status', label: 'STATUS' },
          { key: 'publishDate', label: 'PUBLISH DATE' },
          { key: 'expiryDate', label: 'EXPIRY DATE' },
          { key: 'metricUsers', label: 'REPORTS - USER' },
        ]
      }

      return [
        { key: 'no', label: 'NO' },
        { key: 'ad', label: 'AD' },
        { key: 'status', label: 'STATUS' },
        { key: 'publishDate', label: 'PUBLISH DATE' },
        { key: 'expiryDate', label: 'EXPIRY DATE' },
        { key: 'metricUsers', label: this.metricLabel },
      ]
    },
    filteredRows() {
      const term = this.search.trim().toLowerCase()

      return this.rows.filter(item => {
        const matchesFilter = !this.selectedFilter || item.rawStatus === this.selectedFilter
        const matchesSearch = this.supportsServerSearch || !term || JSON.stringify(item).toLowerCase().includes(term)

        return matchesFilter && matchesSearch
      })
    },
    emptyStateText() {
      if (this.isLoading) return 'Loading report details...'
      if (this.errorMessage && !this.rows.length) return 'Unable to load report details.'
      if (this.search.trim() || this.selectedFilter) return 'No records match the current filters.'

      return 'No records found'
    },
    paginationLabel() {
      if (!this.totalRows && !this.rows.length) return 'Showing 0 entries'

      if (!this.supportsServerSearch && (this.search.trim() || this.selectedFilter)) {
        if (!this.filteredRows.length) {
          return 'Showing 0 items on this page'
        }
        return `Showing ${this.filteredRows.length} item${this.filteredRows.length === 1 ? '' : 's'} on this page`
      }

      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.filteredRows.length - 1

      return `Showing ${start} to ${Math.max(start, end)} of ${this.paginationTotalRows} entries`
    },
    paginationTotalRows() {
      if (this.totalRows > 0) return this.totalRows
      if (this.rows.length > 0) return this.rows.length
      return 1
    },
  },
  watch: {
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchMetricRows()
      }
    },
    metricKey() {
      this.search = ''
      this.selectedFilter = ''
      this.totalRows = 0
      this.rows = []

      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchMetricRows()
      }
    },
    search() {
      if (!this.supportsServerSearch) return

      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        if (this.currentPage !== 1) {
          this.currentPage = 1
        } else {
          this.fetchMetricRows()
        }
      }, 350)
    },
  },
  methods: {
    async loadCategoryOptions() {
      try {
        this.categoryOptions = await fetchAdCategories()
      } catch (error) {
        this.categoryOptions = []
      }
    },
    onCategoryFilterChange() {
      const secondaryId = Number(this.secondaryCategoryId || 0)
      const childIds = secondaryCategoryOptions(this.categoryOptions, this.categoryId)
        .map(option => Number(option.value))
      if (!secondaryId || !childIds.includes(secondaryId)) {
        this.secondaryCategoryId = null
      }
      this.currentPage = 1
      this.fetchMetricRows()
    },
    avatarText(value) {
      return getInitials(value)
    },
    statusVariant(status) {
      switch (status) {
        case 'active':
          return 'light-success'
        case 'inactive':
          return 'light-secondary'
        case 'draft':
          return 'light-warning'
        case 'pending_approval':
          return 'light-primary'
        default:
          return 'light-secondary'
      }
    },
    normalizeMetricUsers(item) {
      if (this.metricKey === 'reports') {
        return item.reports_users || item.report_users || []
      }

      return item[`${this.metricKey}_users`] || []
    },
    metricCount(item) {
      if (this.metricKey === 'reports') {
        return Number(item.reports_count || item.report_count || 0)
      }

      return Number(item[`${this.metricKey}_count`] || 0)
    },
    extraUsersCount(item) {
      if (this.metricKey === 'reports') {
        return Number(item.extra_report || 0)
      }

      return Number(item[`extra_${this.metricKey}`] || 0)
    },
    mapRow(item, index, page, perPage) {
      const name = item.ad_name || item.name || ''
      const rawStatus = String(item.status || '').trim().toLowerCase()
      const metricCount = this.metricCount(item)

      return {
        id: item.id,
        no: String((page - 1) * perPage + index + 1).padStart(2, '0'),
        name,
        thumbnail: resolveMediaUrl(item.thumbnail || ''),
        adInitials: this.avatarText(name),
        rawStatus,
        status: formatStatus(rawStatus),
        publishDate: formatDate(item.publish_date),
        expiryDate: formatDate(item.expiry_date),
        metricCount,
        metricUsers: this.normalizeMetricUsers(item),
        extraUsersCount: this.extraUsersCount(item),
        reportsText: `${metricCount} report${metricCount === 1 ? '' : 's'}`,
        joinedOn: formatDate(item.joined_On || item.joined_on),
        lastActive: item.last_Active || item.last_active || '-',
        contact: item.contact || '-',
        email: item.email || '-',
      }
    },
    onMailAction(item) {
      if (item.email && item.email !== '-') {
        window.location.href = `mailto:${item.email}`
        return
      }
      notifyInfo(this, 'No email available for this follower.')
    },
    onEditAction(item) {
      notifyInfo(this, `Edit follower: ${item.name || 'this user'}`)
    },
    onDeleteAction(item) {
      notifyInfo(this, `Removing followers is not available in the vendor panel yet.${item.name ? ` (${item.name})` : ''}`)
    },
    exportMetricCsv() {
      if (!this.filteredRows.length) return
      const columns = this.tableFields
        .filter(field => field.key !== 'actions')
        .map(field => ({ key: field.key, label: field.label }))
      downloadCsv(`reports-${this.metricKey}.csv`, this.filteredRows, columns)
    },
    parseReportPagination(response, ads = []) {
      const body = response?.data || {}
      const data = body.data && typeof body.data === 'object' ? body.data : {}
      const pagination = body.pagination || data.pagination || body.meta || data.meta || {}

      const page = Number(
        data.current_page ?? pagination.current_page ?? pagination.page ?? body.current_page ?? this.currentPage,
      )
      const perPage = Number(
        data.per_page ?? pagination.per_page ?? pagination.limit ?? body.per_page ?? this.perPage,
      )
      let total = Number(
        data.total
        ?? pagination.total
        ?? body.total
        ?? pagination.total_count
        ?? data.total_count
        ?? 0,
      )
      const lastPage = Number(pagination.last_page ?? pagination.total_pages ?? data.last_page ?? 0)

      if (!total && lastPage > 0 && perPage > 0) {
        total = lastPage * perPage
      }
      if (!total && ads.length > 0) {
        total = (page - 1) * perPage + ads.length
        if (ads.length >= perPage) {
          total = Math.max(total, page * perPage + 1)
        }
      }

      return {
        page: page > 0 ? page : this.currentPage,
        perPage: perPage > 0 ? perPage : this.perPage,
        total: Math.max(0, total),
      }
    },
    async fetchMetricRows() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const payload = {
          page: this.currentPage,
          per_page: this.perPage,
        }

        if (this.supportsServerSearch) {
          payload.search = this.search.trim()
        }
        if (this.showCategoryFilters && this.categoryId) {
          payload.category_id = this.categoryId
        }
        if (this.showCategoryFilters && this.secondaryCategoryId) {
          payload.secondary_category_id = this.secondaryCategoryId
        }

        const response = await vendorPanelApi.reportByMetric(metricPathMap[this.metricKey] || 'likes', payload)
        const data = response?.data?.data || {}
        const ads = Array.isArray(data.ads) ? data.ads : []
        const paginationMeta = this.parseReportPagination(response, ads)

        this.perPage = paginationMeta.perPage
        this.totalRows = paginationMeta.total
        this.rows = ads.map((item, index) => this.mapRow(
          item,
          index,
          paginationMeta.page,
          paginationMeta.perPage,
        ))
      } catch (error) {
        this.rows = []
        this.totalRows = 0
        this.errorMessage = error?.response?.data?.message || 'Unable to load the report details right now.'
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.reports-detail-page {
  .detail-toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.95rem;
    flex-wrap: wrap;
  }

  .toolbar-input { width: 145px; }
  .toolbar-select { width: 140px; }
  .toolbar-export { white-space: nowrap; }

  .detail-alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .detail-table {
    ::v-deep thead th {
      color: #7a758f;
      font-size: 0.78rem;
      font-weight: 700;
      border-top: 1px solid #ececf3;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
      background: #fff;
    }

    ::v-deep tbody td {
      color: #5f5a73;
      font-size: 0.92rem;
      vertical-align: middle;
      padding-top: 0.8rem;
      padding-bottom: 0.8rem;
    }
  }

  .ad-cell {
    display: flex;
    align-items: center;
    gap: 0.55rem;
  }

  .ad-mini-logo {
    flex-shrink: 0;
  }

  .avatar-stack {
    display: flex;
    align-items: center;
  }

  .stack-avatar {
    border: 2px solid #fff;
    margin-left: -6px;

    &:first-child {
      margin-left: 0;
    }
  }

  .more-count {
    margin-left: 0.2rem;
    color: #8a85a0;
    font-size: 0.82rem;
    font-weight: 600;
  }

  .row-actions {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    color: #6f6b86;
  }

  .detail-footer {
    padding: 0.85rem 0.15rem 0;
    margin-top: 0.25rem;
  }
}
</style>
